import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, createBillingCheckout } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/site/Logo";

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const loggedInUser = await login(email, password);
            // Post-login: if we came from /priser with a checkout intent, jump to Stripe
            let checkoutPackage = location.state?.checkoutPackage;
            if (!checkoutPackage) {
                try { checkoutPackage = window.sessionStorage.getItem("msh_post_login_checkout"); } catch { /* noop */ }
            }
            if (checkoutPackage) {
                try { window.sessionStorage.removeItem("msh_post_login_checkout"); } catch { /* noop */ }
                try {
                    const data = await createBillingCheckout(checkoutPackage);
                    if (data?.url) {
                        window.location.href = data.url;
                        return;
                    }
                } catch (err) {
                    setError(formatApiError(err) || "Kunne ikke starte betaling");
                    setLoading(false);
                    return;
                }
            }
            const target = (loggedInUser?.role || "").toLowerCase() === "medarbejder"
                ? "/medarbejder"
                : (location.state?.from || "/dashboard");
            navigate(target, { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 px-4 py-12">
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-6">
                    <Logo height={60} />
                </div>
                <div data-testid="login-card" className="bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-200/70 p-7">
                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Log ind</h1>
                    <p className="text-sm text-slate-500 mt-1">Velkommen tilbage til dit MyServiceHelper dashboard.</p>

                    {/* Google Sign-in — REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                    <button
                        type="button"
                        data-testid="login-google-btn"
                        onClick={() => {
                            const redirectUrl = window.location.origin + "/dashboard";
                            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                        }}
                        className="mt-6 w-full h-11 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 flex items-center justify-center gap-3 font-medium text-slate-700 shadow-sm transition-colors"
                    >
                        <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                            <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.1z"/>
                            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.3-.4-3.5z"/>
                        </svg>
                        <span>Log ind med Google</span>
                    </button>

                    <div className="my-5 flex items-center gap-3">
                        <div className="flex-1 h-px bg-slate-200" />
                        <span className="text-xs uppercase tracking-wider text-slate-400 font-medium">Eller med email</span>
                        <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-slate-700">Email</Label>
                            <div className="relative mt-1.5">
                                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <Input
                                    id="email"
                                    type="email"
                                    required
                                    data-testid="login-email-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-9 h-11"
                                    placeholder="navn@firma.dk"
                                />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-slate-700">Adgangskode</Label>
                            <div className="relative mt-1.5">
                                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <Input
                                    id="password"
                                    type="password"
                                    required
                                    data-testid="login-password-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-9 h-11"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>
                        {error && (
                            <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>
                        )}
                        <div className="flex justify-end">
                            <Link to="/glemt-adgangskode" data-testid="login-forgot-link" className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                                Glemt adgangskode?
                            </Link>
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-btn"
                            className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Log ind
                        </Button>
                    </form>

                    <p className="text-sm text-slate-500 text-center mt-6">
                        Har du ikke en konto?{" "}
                        <Link to="/opret-konto" data-testid="login-register-link" className="text-blue-600 hover:text-blue-700 font-medium">
                            Opret konto
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
